import React, { useState, useEffect, useCallback } from 'react';
import { adminApi, ListAgentTasksParams } from '../../services/adminApi';
import { AgentTask, AgentTaskStatus } from '../../types/admin';
import { Button } from '../ui/Button';
import { TimelineEvent } from './TimelineEvent';
import styles from './AgentTaskViewer.module.css';

const STATUS_OPTIONS: AgentTaskStatus[] = ['pending', 'running', 'completed', 'failed'];
const AGENT_TYPES = [
  'EMAIL_ANALYSIS_AGENT',
  'EMAIL_TRIAGE_AGENT',
  'LINKEDIN_CONTACT_AGENT',
];

export const AgentTaskViewer: React.FC = () => {
  const [tasks, setTasks] = useState<AgentTask[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedTaskId, setExpandedTaskId] = useState<string | null>(null);
  const [expandedTaskDetails, setExpandedTaskDetails] = useState<AgentTask | null>(null);
  const [loadingEvents, setLoadingEvents] = useState(false);

  // Filters
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [agentTypeFilter, setAgentTypeFilter] = useState<string>('');

  // Pagination
  const [page, setPage] = useState(0);
  const limit = 25;

  const fetchTasks = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const params: ListAgentTasksParams = {
        limit,
        offset: page * limit,
      };

      if (statusFilter) params.status = statusFilter;
      if (agentTypeFilter) params.agentName = agentTypeFilter;

      const response = await adminApi.listAgentTasks(params);
      setTasks(response.tasks);
      setTotal(response.total);
    } catch (err) {
      console.error('Error fetching agent tasks:', err);
      setError('Failed to load agent tasks');
    } finally {
      setLoading(false);
    }
  }, [page, statusFilter, agentTypeFilter]);

  useEffect(() => {
    fetchTasks();
  }, [fetchTasks]);

  const toggleExpanded = async (taskId: string) => {
    if (expandedTaskId === taskId) {
      // Collapse
      setExpandedTaskId(null);
      setExpandedTaskDetails(null);
    } else {
      // Expand - fetch task with events
      setExpandedTaskId(taskId);
      setLoadingEvents(true);
      try {
        const taskWithEvents = await adminApi.getAgentTaskWithEvents(taskId);
        setExpandedTaskDetails(taskWithEvents);
      } catch (err) {
        console.error('Error fetching task events:', err);
        setError('Failed to load task events');
      } finally {
        setLoadingEvents(false);
      }
    }
  };

  const formatDuration = (startedAt: string, completedAt?: string): string => {
    if (!completedAt) return '-';
    const start = new Date(startedAt).getTime();
    const end = new Date(completedAt).getTime();
    const durationMs = end - start;
    if (durationMs < 1000) return `${durationMs}ms`;
    return `${(durationMs / 1000).toFixed(2)}s`;
  };

  const formatDate = (dateStr?: string): string => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString();
  };

  const getStatusClass = (status: AgentTaskStatus): string => {
    return styles[`status${status.charAt(0).toUpperCase() + status.slice(1)}`] || '';
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <div className={styles.container}>
      <div className="mobile-page-title-anchor">
        <h1 className={styles.title}>Agent Tasks</h1>
      </div>

      {/* Filters */}
      <div className={styles.filters}>
        <div className={styles.filterGroup}>
          <label htmlFor="status-filter">Status:</label>
          <select
            id="status-filter"
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}
          >
            <option value="">All</option>
            {STATUS_OPTIONS.map(status => (
              <option key={status} value={status}>{status}</option>
            ))}
          </select>
        </div>

        <div className={styles.filterGroup}>
          <label htmlFor="agent-type-filter">Agent Type:</label>
          <select
            id="agent-type-filter"
            value={agentTypeFilter}
            onChange={(e) => { setAgentTypeFilter(e.target.value); setPage(0); }}
          >
            <option value="">All</option>
            {AGENT_TYPES.map(type => (
              <option key={type} value={type}>{type}</option>
            ))}
          </select>
        </div>

        <Button variant="ghost" size="sm" onClick={fetchTasks}>
          Refresh
        </Button>
      </div>

      {/* Error State */}
      {error && <div className={styles.error}>{error}</div>}

      {/* Loading State */}
      {loading && <div className={styles.loading}>Loading...</div>}

      {/* Task Table */}
      {!loading && !error && (
        <>
          <div className={styles.tableWrapper}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Agent</th>
                  <th>Status</th>
                  <th>Trigger</th>
                  <th>Duration</th>
                  <th>Started</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {tasks.map(task => (
                  <React.Fragment key={task.id}>
                    <tr className={expandedTaskId === task.id ? styles.expanded : ''}>
                      <td>{task.agentName}</td>
                      <td>
                        <span className={`${styles.statusBadge} ${getStatusClass(task.status)}`}>
                          {task.status}
                        </span>
                      </td>
                      <td>{task.triggerType}</td>
                      <td>{formatDuration(task.startedAt, task.completedAt)}</td>
                      <td>{formatDate(task.startedAt)}</td>
                      <td>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => toggleExpanded(task.id)}
                        >
                          {expandedTaskId === task.id ? 'Hide' : 'Details'}
                        </Button>
                      </td>
                    </tr>
                    {expandedTaskId === task.id && (
                      <tr className={styles.detailRow}>
                        <td colSpan={6}>
                          {loadingEvents ? (
                            <div className={styles.loadingEvents}>Loading events...</div>
                          ) : expandedTaskDetails ? (
                            <div className={styles.taskDetails}>
                              {/* Summary Section */}
                              <div className={styles.summarySection}>
                                <div className={styles.detailSection}>
                                  <h4>Summary</h4>
                                  <dl>
                                    <dt>Model:</dt>
                                    <dd>{expandedTaskDetails.model || '-'}</dd>
                                    <dt>Tokens:</dt>
                                    <dd>
                                      {expandedTaskDetails.totalTokens ? (
                                        <>
                                          {expandedTaskDetails.totalTokens} total
                                          ({expandedTaskDetails.promptTokens} prompt + {expandedTaskDetails.completionTokens} completion)
                                        </>
                                      ) : '-'}
                                    </dd>
                                    <dt>Duration:</dt>
                                    <dd>{formatDuration(task.startedAt, task.completedAt)}</dd>
                                    <dt>Iterations:</dt>
                                    <dd>{expandedTaskDetails.iterations || '-'}</dd>
                                    <dt>Tool Calls:</dt>
                                    <dd>{expandedTaskDetails.toolCallsMade || '-'}</dd>
                                  </dl>
                                </div>
                                {expandedTaskDetails.inputSummary && (
                                  <div className={styles.detailSection}>
                                    <h4>Input</h4>
                                    <p className={styles.inputSummary}>{expandedTaskDetails.inputSummary}</p>
                                  </div>
                                )}
                                {expandedTaskDetails.errorMessage && (
                                  <div className={styles.detailSection}>
                                    <h4>Error</h4>
                                    <pre className={styles.errorOutput}>{expandedTaskDetails.errorMessage}</pre>
                                  </div>
                                )}
                              </div>

                              {/* Timeline Section */}
                              {expandedTaskDetails.events && expandedTaskDetails.events.length > 0 && (
                                <div className={styles.timelineSection}>
                                  <h4 className={styles.timelineTitle}>
                                    Execution Timeline ({expandedTaskDetails.events.length} events)
                                  </h4>
                                  <div className={styles.timeline}>
                                    {expandedTaskDetails.events.map((event) => (
                                      <TimelineEvent key={event.id} event={event} />
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          ) : (
                            <div className={styles.taskDetails}>
                              <div className={styles.detailSection}>
                                <h4>Timestamps</h4>
                                <dl>
                                  <dt>Started:</dt>
                                  <dd>{formatDate(task.startedAt)}</dd>
                                  <dt>Completed:</dt>
                                  <dd>{formatDate(task.completedAt)}</dd>
                                </dl>
                              </div>
                            </div>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className={styles.pagination}>
            <span>
              {total === 0
                ? 'No results'
                : `Showing ${page * limit + 1}-${Math.min((page + 1) * limit, total)} of ${total}`
              }
            </span>
            <div className={styles.pageButtons}>
              <Button
                variant="secondary"
                size="sm"
                disabled={page === 0}
                onClick={() => setPage(p => p - 1)}
              >
                Previous
              </Button>
              <Button
                variant="secondary"
                size="sm"
                disabled={page >= totalPages - 1}
                onClick={() => setPage(p => p + 1)}
              >
                Next
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};
